import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useNotices } from '../contexts/NoticeContext';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

const NoticeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notices, fetchNoticeContent } = useNotices();
  // 본문은 목록 조회에서 제외했으므로 상세에서 단건으로 읽는다.
  const [content, setContent] = useState('');

  const notice = notices.find(n => n.id === Number(id));

  // 페이지 진입 시 맨 위로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!id) return;
    fetchNoticeContent(Number(id)).then(setContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 파일 크기 포맷
  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };


  if (!notice) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 pt-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 py-12">
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-500">공지사항을 찾을 수 없습니다.</p>
              <button
                onClick={() => navigate('/notice')}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                목록으로
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header */}
            <div className="border-b border-gray-200 px-8 py-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{notice.title}</h1>
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>담당부서: {notice.department}</span>
                <span>작성일: {new Date(notice.created_at).toLocaleDateString('ko-KR')}</span>
              </div>
            </div>

            {/* Content */}
            <div className="px-8 py-8">
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: content || notice.content || '' }}
              />
            </div>

            {/* 첨부파일 */}
            {notice.file_url && (
              <div className="px-8 pb-6">
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">첨부파일</h3>
                  <a
                    href={notice.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={notice.file_name || '첨부파일'}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium">{notice.file_name}</span>
                    <span className="text-xs text-gray-400">({formatFileSize(notice.file_size)})</span>
                  </a>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-6 flex justify-end">
              <button
                onClick={() => navigate('/notice')}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NoticeDetail;
